// src/components/customers/CustomerHeader.jsx
import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { updateCustomer } from "../../services/customerService";
import Swal from "sweetalert2";

/**
 * CustomerHeader Component
 *
 * Displays customer information header with edit functionality and navigation
 *
 * @param {Object} props - Component props
 * @param {Object} props.customer - Customer object
 * @param {Function} props.onCustomerUpdate - Optional callback when customer is updated
 */
const CustomerHeader = ({ customer, onCustomerUpdate }) => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const location = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    phone: "",
    postCode: "",
    location: "",
    billingLocation: "",
    billingAddressSame: true,
    // Landlord details - all optional
    landlordName: "",
    landlordEmail: "",
    landlordPhone: "",
    landlordPostCode: "",
  });

  // Initialize form data when modal opens
  const openEditModal = () => {
    setEditFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      postCode: customer.postCode || "",
      location: customer.location || "",
      billingLocation: customer.billingLocation || "",
      billingAddressSame: customer.billingAddressSame !== false, // Default to true if undefined
      // Landlord fields are absent on pre-existing customers - default to ""
      landlordName: customer.landlordName || "",
      landlordEmail: customer.landlordEmail || "",
      landlordPhone: customer.landlordPhone || "",
      landlordPostCode: customer.landlordPostCode || "",
    });
    setIsEditModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!editFormData.name.trim()) {
      showMessage("Please enter a customer name", "error");
      return;
    }

    if (!editFormData.phone.trim()) {
      showMessage("Please enter a phone number", "error");
      return;
    }

    if (!editFormData.postCode.trim()) {
      showMessage("Please enter a post code", "error");
      return;
    }

    if (!editFormData.location.trim()) {
      showMessage("Please enter a primary address", "error");
      return;
    }

    // Validate billing address if it's different from primary
    if (
      !editFormData.billingAddressSame &&
      !editFormData.billingLocation.trim()
    ) {
      showMessage(
        'Please enter a billing address or check the "same as primary" option',
        "error"
      );
      return;
    }

    // Landlord fields are all optional. Only format-check email and phone, and
    // only when filled in - same rules as the customer's own email/phone.
    if (
      editFormData.landlordEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.landlordEmail.trim())
    ) {
      showMessage("Please enter a valid landlord email address", "error");
      return;
    }

    if (
      editFormData.landlordPhone.trim() &&
      !/^\d{11}$/.test(editFormData.landlordPhone.trim())
    ) {
      showMessage("Landlord phone number must be 11 digits", "error");
      return;
    }

    try {
      const updatedCustomer = {
        ...customer,
        name: editFormData.name.trim(),
        email: editFormData.email.trim(),
        phone: editFormData.phone.trim(),
        postCode: editFormData.postCode.trim(),
        location: editFormData.location.trim(),
        billingLocation: editFormData.billingAddressSame
          ? editFormData.location.trim()
          : editFormData.billingLocation.trim(),
        billingAddressSame: editFormData.billingAddressSame,
        landlordName: editFormData.landlordName.trim(),
        landlordEmail: editFormData.landlordEmail.trim(),
        landlordPhone: editFormData.landlordPhone.trim(),
        landlordPostCode: editFormData.landlordPostCode.trim(),
      };

      // Get the correct customer ID - try multiple sources
      const actualCustomerId = customerId || customer?.id || customer?._id;

      if (!actualCustomerId) {
        showMessage("Error: Customer ID not found", "error");
        return;
      }

      await updateCustomer(actualCustomerId, updatedCustomer);

      // Update the parent component with new customer data
      if (onCustomerUpdate) {
        onCustomerUpdate(updatedCustomer);
      }

      setIsEditModalOpen(false);
      showMessage("Customer updated successfully", "success");

      // Force a page refresh to ensure data is updated
      window.location.reload();
    } catch (error) {
      console.error("Error updating customer:", error);
      showMessage("Failed to update customer. Please try again.", "error");
    }
  };

  // Check if we're currently on the jobs page
  const isOnJobsPage =
    location.pathname.includes("/jobs") &&
    !location.pathname.includes("/jobs/") &&
    !location.pathname.includes("/rooms");

  // Go back to customer jobs page
  const goBackToJobs = () => {
    // Try to get the correct customer ID
    const actualCustomerId = customerId || customer?.id || customer?._id;

    if (!actualCustomerId || actualCustomerId === "undefined") {
      console.error(
        "No valid customer ID found, redirecting to customers list"
      );
      navigate("/customers");
      return;
    }

    navigate(`/customers/${actualCustomerId}/jobs`);
  };

  // Go back to customers list
  const goBackToCustomers = () => {
    navigate("/customers");
  };

  // Show toast message
  const showMessage = (msg = "", type = "success") => {
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { container: "toast" },
    }).fire({
      icon: type,
      title: msg,
      padding: "10px 20px",
    });
  };

  if (!customer) {
    return null;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
        {/* Customer Info */}
        <div className="flex items-center mb-4">
          {/* Customer Avatar */}
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
            <span className="text-xl sm:text-2xl font-bold text-indigo-600">
              {customer.name ? customer.name.charAt(0).toUpperCase() : "C"}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
              {customer.name}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Customer Information
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 mb-4">
          {/* Conditional Back Button */}
          {isOnJobsPage ? (
            // If on jobs page, show "Back to Customers" button
            <button
              onClick={goBackToCustomers}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
            >
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Customers
            </button>
          ) : (
            // If on other pages (like rooms), show "Back to Jobs" button
            <button
              onClick={goBackToJobs}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
            >
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Jobs
            </button>
          )}

          <button
            onClick={openEditModal}
            className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
          >
            <svg
              className="w-5 h-5 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Customer
          </button>
        </div>

        {/* Customer Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Email */}
          <div className="flex items-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500">Email</p>
              <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                {customer.email || "Not specified"}
              </p>
            </div>
          </div>

          {/* Phone */}
          <div className="flex items-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500">Phone</p>
              <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                {customer.phone || "Not specified"}
              </p>
            </div>
          </div>

          {/* Post Code */}
          <div className="flex items-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500">Post Code</p>
              <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                {customer.postCode || "Not specified"}
              </p>
            </div>
          </div>

          {/* Address */}
          <div className="flex items-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500">Address</p>
              <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                {customer.location || "Not specified"}
              </p>
            </div>
          </div>
        </div>

        {/* Landlord Details - optional, so only rendered when the customer
            actually has at least one landlord field filled in. Customers with
            no landlord data look exactly as they did before this section. */}
        {(customer.landlordName ||
          customer.landlordEmail ||
          customer.landlordPhone ||
          customer.landlordPostCode) && (
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-3">Landlord</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Landlord Name */}
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-500">Name</p>
                  <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                    {customer.landlordName || "Not specified"}
                  </p>
                </div>
              </div>

              {/* Landlord Email */}
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-500">Email</p>
                  <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                    {customer.landlordEmail || "Not specified"}
                  </p>
                </div>
              </div>

              {/* Landlord Phone */}
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                    {customer.landlordPhone || "Not specified"}
                  </p>
                </div>
              </div>

              {/* Landlord Post Code */}
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-500">Post Code</p>
                  <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                    {customer.landlordPostCode || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Customer Modal */}
      {isEditModalOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsEditModalOpen(false)}
          />

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Customer
                </h3>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-6">
                  {/* Customer Details Section */}
                  <div>
                    <h4 className="text-base font-medium text-gray-900 mb-4">
                      Customer Details
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name */}
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={editFormData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          required
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Email{" "}
                          <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={editFormData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={editFormData.phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          required
                        />
                      </div>

                      {/* Post Code */}
                      <div>
                        <label
                          htmlFor="postCode"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Post Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="postCode"
                          name="postCode"
                          value={editFormData.postCode}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Landlord Details Section - every field optional */}
                  <div>
                    <h4 className="text-base font-medium text-gray-900 mb-4">
                      Landlord Details
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Landlord Name */}
                      <div>
                        <label
                          htmlFor="landlordName"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Name <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="text"
                          id="landlordName"
                          name="landlordName"
                          value={editFormData.landlordName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Landlord Email */}
                      <div>
                        <label
                          htmlFor="landlordEmail"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Email{" "}
                          <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="email"
                          id="landlordEmail"
                          name="landlordEmail"
                          value={editFormData.landlordEmail}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Landlord Phone */}
                      <div>
                        <label
                          htmlFor="landlordPhone"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Phone Number{" "}
                          <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="tel"
                          id="landlordPhone"
                          name="landlordPhone"
                          value={editFormData.landlordPhone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Landlord Post Code */}
                      <div>
                        <label
                          htmlFor="landlordPostCode"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Post Code{" "}
                          <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="text"
                          id="landlordPostCode"
                          name="landlordPostCode"
                          value={editFormData.landlordPostCode}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Information Section */}
                  <div>
                    <h4 className="text-base font-medium text-gray-900 mb-4">
                      Address Information
                    </h4>

                    {/* Primary Address */}
                    <div className="mb-4">
                      <label
                        htmlFor="location"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Primary Address <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="location"
                        name="location"
                        value={editFormData.location}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        required
                      />
                    </div>

                    {/* Billing Address Checkbox */}
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="billingAddressSame"
                        name="billingAddressSame"
                        checked={editFormData.billingAddressSame}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label
                        htmlFor="billingAddressSame"
                        className="ml-2 text-sm text-gray-700"
                      >
                        Billing address is the same as primary address
                      </label>
                    </div>

                    {/* Billing Address Field - Only show if different from primary */}
                    {!editFormData.billingAddressSame && (
                      <div>
                        <label
                          htmlFor="billingLocation"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Billing Address{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="billingLocation"
                          name="billingLocation"
                          value={editFormData.billingLocation}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                          required={!editFormData.billingAddressSame}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Update Customer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerHeader;
